import { useEffect, useMemo, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import { Box, Group, LoadingOverlay, NativeSelect } from '@mantine/core';
import { getCoreRowModel, useReactTable, getExpandedRowModel } from '@tanstack/react-table';
import Head from './Head';
import Body from './Body';
import Toolbar from './Toolbar';
import TablePaginationRow from './TablePaginationRow';
import type { TableInstanceProps } from '@/types/table';
import classes from './Table.module.css';
import Upsell from '@/components/content/upsell/Upsell';
import { visibleNameIncludes } from '@/utils/visibleName';

const TableInstance = <TTableData extends unknown>({
  instanceConfiguration,
  data,
  pageCount,
  id,
  isError = false,
  isLoading = false,
  errorMessage = 'There was an error loading the data',
  noContentMessage = 'No data available',
  subRowComponent,
  customActions,
  options,
  onRowSelection,
  renderSelection,
  groupBy,
  csv,
  footerEnd,
  highlightRow,
  flagRow,
  showUpsellPlaceholder = false,
  upsellModules,
}: TableInstanceProps<TTableData>) => {
  const selectionRef = useRef<HTMLDivElement>(null);
  const pagination = instanceConfiguration?.state?.pagination;
  const pageSize = pagination?.pageSize || 0;
  const { hideSearch, hideToolbar } = options || {};

  const includePageCount = useMemo(
    () => (pageCount ? { pageCount: Math.ceil(pageCount / pageSize) } : {}),
    [pageCount, pageSize]
  );

  const instance = useReactTable<TTableData>({
    data,
    ...instanceConfiguration,
    ...includePageCount,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    enableSortingRemoval: false,
    globalFilterFn: (row, columnId, filterValue: string) => {
      const search = typeof filterValue === 'number' ? String(filterValue) : filterValue;
      let value = row.getValue(columnId) as string;

      if (typeof value === 'number') value = String(value);

      return visibleNameIncludes(value, search);
    },
  });

  const rowSelectionState = Object.values(instance.getState().rowSelection);

  useEffect(() => {
    if (onRowSelection) {
      onRowSelection(
        instance.getSelectedRowModel().flatRows.map((row) => row.original),
        instance.getState().rowSelection
      );
    }
  }, [rowSelectionState.length]);

  const hasPages = instance.getRowModel().rows.length > 0;
  const message = isError ? errorMessage : noContentMessage;
  const selectedItems = Object.keys(instance.getState().rowSelection).length;
  const showPagination = !options?.hidePagination && hasPages;
  const showCsvOnly = !!(csv && hasPages && !showPagination);
  const totalRecords = pageCount ?? instance.getPrePaginationRowModel().rows.length;

  return (
    <div className={classes.tableWrapper}>
      <div className={`${classes.tableMain} elevated-panel elevated-panel--padded`}>
        {!hideToolbar && (
          <Toolbar tableInstance={instance} hideSearch={hideSearch} customActions={customActions} />
        )}

        {(groupBy || renderSelection || showCsvOnly) && (
          <Group justify="flex-end">
            {groupBy && (
              <NativeSelect
                value={instance?.getState().grouping[0]}
                aria-label="Group By"
                onChange={(e) => {
                  instance?.setGrouping(() => [e.target.value]);
                }}
                data={groupBy as Array<string>}
                size="xs"
              />
            )}

            {/* Selection */}
            <CSSTransition
              in={selectedItems > 0}
              nodeRef={selectionRef}
              timeout={500}
              className={classes.selectionWrapper}
              classNames={{
                enter: classes.enter,
                enterActive: classes.enterActive,
                exit: classes.exit,
                exitActive: classes.exitActive,
              }}
              unmountOnExit
            >
              <Box ref={selectionRef} mr="auto">
                {renderSelection && renderSelection({ nItems: selectedItems })}
              </Box>
            </CSSTransition>

            {showCsvOnly && <Box ml="auto">{csv}</Box>}
          </Group>
        )}

        {showUpsellPlaceholder ? (
          <Upsell requiredModules={upsellModules} isTableTabUpsell />
        ) : (
          <div className={classes.tableOverflow}>
            <table className={`${classes.table} v2-table`} id={id}>
              <Head tableInstance={instance} />

              {hasPages && (
                <Body
                  instance={instance}
                  subRowComponent={subRowComponent}
                  highlightRow={highlightRow}
                  flagRow={flagRow}
                />
              )}

              {(isError || !hasPages) && (
                <tbody>
                  <tr>
                    <Box
                      component="td"
                      className={classes.messageTd}
                      colSpan={instance.getVisibleFlatColumns().length}
                    >
                      {message}
                    </Box>
                  </tr>
                </tbody>
              )}

              {showPagination && (
                <TablePaginationRow
                  colSpan={instance.getVisibleFlatColumns().length}
                  page={instance.getState().pagination.pageIndex + 1}
                  pageCount={instance.getPageCount()}
                  totalRecords={totalRecords}
                  pageSize={instance.getState().pagination.pageSize}
                  canNext={instance.getCanNextPage()}
                  canPrevious={instance.getCanPreviousPage()}
                  next={instance.nextPage}
                  previous={instance.previousPage}
                  csv={csv}
                  end={footerEnd}
                  onPageSizeChange={(size) => instance.setPageSize(size)}
                  onPageChange={(pageIndex) => instance.setPageIndex(pageIndex)}
                  hidePageSize={options?.hidePageSize}
                />
              )}
            </table>
          </div>
        )}

        <LoadingOverlay
          visible={isLoading}
          zIndex={99}
          overlayProps={{ radius: 'sm', blur: 1 }}
          loaderProps={{ size: 'xl' }}
        />
      </div>
    </div>
  );
};

export default TableInstance;
